import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";

import { db, auth } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// 🔴 Google Apps Script URL
const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbzo7zR5KcbuTNRLjgD_J1SYFvDZq33ZSSqhXgAyEy77a-_rAfmRebpZVKMgISdcjzYC/exec";

export default function PlanRequest() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const plan = state?.plan;
  const user = auth.currentUser;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!plan) {
    return (
      <Container sx={{ pt: 10 }}>
        <Alert severity="error">No plan selected.</Alert>
      </Container>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\d{0,10}$/.test(value)) return;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      err.email = "Enter a valid email";
    if (!/^\d{10}$/.test(form.phone))
      err.phone = "Phone must be 10 digits";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const sendToGoogleSheet = async (payload) => {
    const body = new URLSearchParams(payload).toString();
    await fetch(GOOGLE_SCRIPT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await addDoc(collection(db, "plan_requests"), {
        userId: user?.uid || null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        planId: plan.planId,
        planTitle: plan.title,
        billingCycle: plan.billingCycle,
        price: plan.price,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      await sendToGoogleSheet({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        service: plan.title + " Plan",
        category: "Pricing Plan",
        price: plan.price,
      });

      setSuccess(true);
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      alert("Failed to order plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 10, pb: 8 }}>
      {/* 🔹 PAGE TITLE */}
      <Typography variant="h4" mb={3} fontWeight={600}>
        Order Plan
      </Typography>

      {/* PLAN SUMMARY */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={600}>{plan.title} Plan</Typography>
          <Typography color="text.secondary">
            Billing: {plan.billingCycle}
          </Typography>
          <Typography mt={1} fontWeight={600}>
            Price: ₹{plan.price}
          </Typography>
        </CardContent>
      </Card>

      {/* FORM */}
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Your Details
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            <TextField
              label="Full Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
            />

            <TextField
              label="Email Address"
              name="email"
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
            />

            <TextField
              label="Phone Number"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone || "10-digit mobile number"}
              fullWidth
            />

            <TextField
              label="Message (Optional)"
              name="message"
              value={form.message}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />

            {/* 🔹 BUTTON TEXT */}
            <Button
              variant="contained"
              disabled={loading}
              sx={{ mt: 1 }}
              onClick={handleSubmit}
            >
              {loading ? "Processing..." : "Order Now"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* 🔹 SUCCESS MESSAGE */}
      <Snackbar open={success} autoHideDuration={3000}>
        <Alert severity="success">
          Plan ordered successfully 📩
        </Alert>
      </Snackbar>
    </Container>
  );
}
